'use client';

import { type Editor, Spinner } from '@common/components';
import { Button, CustomEditor } from '@common/components';
import { SvgMailSend } from '@common/icon';
import { cn,CustomError } from '@common/utils';
import type { SaveWelloCommentResponse } from '@generatedapi/auth';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Fragment, Suspense, useEffect, useRef, useState } from 'react';

import type { COMMENT_TYPE } from '@/api';
import { AuthApi, AuthQueryKey, UnauthQueryOptions } from '@/api/v2';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useAuthStore } from '@/stores/AuthStore';

import { BottomNavPortal } from '../client';

interface CommentFieldContentProps {
  initOnFocus?: boolean;
  replyTo?: string;
  contentId: number;
  commentType: COMMENT_TYPE;
  rootCommentId?: number;
  parentCommentId?: number;
  isLogin: boolean;
  onSuccess?: (response?: SaveWelloCommentResponse) => void;
  topDivider?: boolean;
  className?: string;
}

const CommentFieldContent = ({
  initOnFocus,
  replyTo,
  contentId,
  commentType,
  rootCommentId,
  parentCommentId,
  isLogin,
  onSuccess,
  topDivider = true,
  className,
}: CommentFieldContentProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const startText = replyTo ? `@${replyTo}` : '';

  const protectedPrefix = startText
    ? `<p class="min-h-24 typo-body3 font-400 Gray-black"><span types="listItem,starterKit" style="color: rgb(51, 102, 255)">${startText}</span>&nbsp;`
    : null;

  const minHtmlLength = replyTo ? protectedPrefix!.length : 0;

  const tempContentForProtectedPrefix = useRef('');
  const editorRef = useRef<Editor | null>(null);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const handleSubmitDisabled = (editor: Editor) => {
    let text = editor.getText({
      blockSeparator: '\n',
    });

    if (startText && text.startsWith(startText)) {
      text = text.slice(startText.length + 1);
    }

    const isDisabled = !text.length;

    setIsSubmitDisabled(isDisabled);
  };

  const initContent = (editor: Editor) => {
    if (protectedPrefix) {
      tempContentForProtectedPrefix.current = protectedPrefix;

      editor.commands.setContent(protectedPrefix);
    } else {
      editor.commands.setContent('');
    }
  };

  useEffect(() => {
    if (!protectedPrefix || !editorRef.current) return;
    editorRef.current.commands.setContent(protectedPrefix);
  }, [protectedPrefix]);

  const postComment = useMutation({
    mutationFn: (contents: string) =>
      AuthApi['post:/wello/v2/auth/wello-comment']({
        body: {
          comment_type_cd: commentType,
          content_id: contentId,
          contents,
          parent_wello_comment_id: parentCommentId,
          root_wello_comment_id: rootCommentId,
        },
      }),
    meta: {
      reset: [
        AuthQueryKey['get:/wello/v2/auth/wello-comment'](),
        AuthQueryKey['get:/wello/v2/auth/policy/feedback/{policy_id}'](),
        AuthQueryKey[
          'get:/wello/v2/auth/contents/feedback/{best_contents_id}'
        ](),
        AuthQueryKey['get:/wello/v2/auth/wello-comment/my'](),
        AuthQueryKey['get:/wello/v2/auth/community/my'](),
        AuthQueryKey[
          'get:/wello/v2/auth/hometown-news/feedback/{hometown_news_id}'
        ](),
        AuthQueryKey[
          'get:/wello/v2/auth/community/free-talk/feedback/{community_id_idx}'
        ](),
        AuthQueryKey[
          'get:/wello/v2/auth/community/policy-talk/feedback/{community_id_idx}'
        ](),
        AuthQueryKey[
          'get:/wello/v2/auth/community/wello-news/feedback/{community_id_idx}'
        ](),
      ],
    },

    onSuccess: async ({ data }) => {
      const context = data?.context;

      const { current: editor } = editorRef;

      if (!editor) throw new CustomError();

      initContent(editor);

      handleSubmitDisabled(editor);

      onSuccess?.(context);

      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    },
  });

  const router = useCustomRouter();

  return (
    <div
      className={cn(
        'w-full p-16 animate-duration-150',
        {
          'border-t border-Gray-200': topDivider,
        },
        className,
      )}
    >
      <CustomEditor.Provider
        placeholder="댓글을 입력해주세요."
        onBlur={() => setIsFocused(false)}
        onCreate={({ editor }) => {
          initContent(editor);

          if (initOnFocus) {
            editor.commands.focus();
          }

          editorRef.current = editor;

          handleSubmitDisabled(editor);
        }}
        onFocus={() => {
          if (isLogin) {
            setIsFocused(true);
          } else {
            const url = new URL(window.location.href);

            router.push(
              ROUTES.LOGIN.withSearchParams({
                searchParams: {
                  redirect: url.pathname + url.search,
                },
              }),
            );
          }
        }}
        onUpdate={({ editor }) => {
          if (protectedPrefix) {
            const content = editor.getHTML();

            if (!content.startsWith(protectedPrefix)) {
              if (content.length <= minHtmlLength) {
                editor.commands.setContent(protectedPrefix);
              } else {
                return editor.commands.setContent(
                  tempContentForProtectedPrefix.current,
                );
              }
            }

            tempContentForProtectedPrefix.current = content;
          }

          handleSubmitDisabled(editor);
        }}
      >
        <form
          className={cn(
            'flex max-h-96 rounded-small border border-Gray-300 bg-Gray-white transition-[border] duration-150',
            {
              'border-Gray-900': isFocused,
            },
          )}
          onSubmit={(e) => {
            e.preventDefault();

            const { current: editor } = editorRef;

            if (!editor) throw new CustomError();

            let text = editor.getText({
              blockSeparator: '\n',
            });

            if (startText) {
              text = text.replace(startText, '');
            }

            text = text.trim();

            if (!text.length) throw new CustomError();

            postComment.mutate(text);
          }}
        >
          <CustomEditor.Contents className="my-11 ml-16 flex-1 p-0 hide-scrollbar typo-body3" />
          {isSubmitDisabled ? null : (
            <Button
              className="mt-auto h-46 animate-fade"
              dimensions="H40"
              disabled={postComment.isPending}
              theme="primary-text"
              type="submit"
            >
              {postComment.isPending ? (
                <Spinner size={20} />
              ) : (
                <SvgMailSend className="size-20 text-inherit" />
              )}
            </Button>
          )}
        </form>
      </CustomEditor.Provider>
    </div>
  );
};

const ReplyCommentField = (
  props: Omit<CommentFieldContentProps, 'replyTo' | 'parentCommentId'> & {
    parentCommentId: number;
  },
) => {
  const { data: nickname } = useSuspenseQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/wello-comment/{welloCommentId}'
    ]({
      path: {
        welloCommentId: props.parentCommentId,
      },
    }),
    select: (data) => data.context?.nick_name,
  });

  return <CommentFieldContent {...props} replyTo={nickname} />;
};

const FixedArea = () => <div className="h-81 w-full" />;

interface CommentFieldProps
  extends Omit<CommentFieldContentProps, 'isLogin' | 'replyTo'> {
  withPortal?: boolean;
}

export const CommentField = ({
  parentCommentId,
  withPortal = true,
  ...restProps
}: CommentFieldProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  const Container = withPortal ? BottomNavPortal : Fragment;

  return (
    <Container>
      {isLogin !== undefined ? (
        <Suspense fallback={<FixedArea />}>
          {parentCommentId ? (
            <ReplyCommentField
              {...restProps}
              isLogin={isLogin}
              parentCommentId={parentCommentId}
            />
          ) : (
            <CommentFieldContent {...restProps} isLogin={isLogin} />
          )}
        </Suspense>
      ) : (
        <FixedArea />
      )}
    </Container>
  );
};
